import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    QueryList,
    ViewChildren,
    inject,
    input,
    Input,
    OnInit,
    output,
    ViewEncapsulation,
    effect,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { WidgetContentSitemapChildItemsComponentBase } from '@fi-shared/widget/sitemap/child-items-base';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ISiteMapContentItem } from '@fi-shared/models/site-map';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { Observable } from 'rxjs';
import { SiteMapHelperService } from '@fi-shared/fi-shared-services/site-map-helper.service';
import { fiGetMediaBankFileIcon } from '@fi-shared/utils';
import { ISitemapWidgetTagExpandedChange, SitemapWidgetHelperService } from '../sitemap/sitemap-widget-helper.service';

@Component({
    selector: 'fi-widget-content-sitemap-child-items',
    templateUrl: './sitemap-child-items.component.html',
    styleUrl: './fi-widget-content-sitemap-child-items.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-widget-content-sitemap-child-items' },
    standalone: false,
})
export class WidgetContentSitemapChildItemsComponent
    extends WidgetContentSitemapChildItemsComponentBase
    implements OnInit, AfterViewInit
{
    @Input() updateChildComponent$ = new Observable<void>();
    contentIndentPx = input(16);
    contentLevel = input(1);
    isExpanded = input<boolean>(false);
    isAllExpanded = input<boolean>(false);
    expandPanels = input<boolean>(false);
    expandPanelsRequest = input(0);
    hideLevelOneItemsOnCollapse = input(false);
    tagExpandedChange = output<ISitemapWidgetTagExpandedChange>();

    @ViewChildren('namePrefixSpan') private namePrefixSpans: QueryList<ElementRef<HTMLElement>>;

    private readonly destroyRef = inject(DestroyRef);
    private readonly namePrefixWidths = signal<ReadonlyMap<string, number>>(new Map());
    private readonly expandedItemKeys = signal<ReadonlySet<string>>(new Set());
    private readonly sitemapWidgetHelperService = inject(SitemapWidgetHelperService);

    private _expandPanelsEffect = effect(() => {
        this.expandPanelsRequest();
        const isExpanded = this.expandPanels() || this.isExpanded();
        this.expandedItemKeys.set(isExpanded ? new Set(this.getExpandableChildItemKeys()) : new Set());
    });

    protected isMainTagItemHidden(item: ISiteMapContentItem): boolean {
        return this.sitemapWidgetHelperService.isLevelOneContentItemHiddenOnCollapse({
            item,
            hideLevelOneItemsOnCollapse: this.hideLevelOneItemsOnCollapse(),
        });
    }

    constructor(
        private stateService: FiStateService,
        private mediaBankHelperService: MediaBankHelperService,
        siteMapHelperService: SiteMapHelperService,
        private cdr: ChangeDetectorRef,
    ) {
        super(siteMapHelperService);
    }

    public ngOnInit(): void {
        this.updateChildComponent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            setTimeout(() => this.measureNamePrefixWidthsFromDom());
            this.cdr.detectChanges();
        });
    }

    public ngAfterViewInit(): void {
        this.namePrefixSpans.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.measureNamePrefixWidthsFromDom();
        });
        this.measureNamePrefixWidthsFromDom();
    }

    protected getItemKey(item: ISiteMapContentItem): string {
        return this.sitemapWidgetHelperService.getItemKey(item);
    }

    protected isItemExpanded(item: ISiteMapContentItem): boolean {
        return this.expandedItemKeys().has(this.getItemKey(item));
    }

    protected getContentItemIndentPx(item: ISiteMapContentItem): number {
        return this.sitemapWidgetHelperService.getContentItemIndentPx({
            item,
            settings: this.siteMapSettings,
            contentIndentPx: this.contentIndentPx(),
            contentLevel: this.contentLevel(),
            isThumbnailLevelWithContentItems: this.isThumbnailLevelWithContentItems(),
        });
    }

    protected getNestedContentIndentPx(expandedItem: ISiteMapContentItem): number {
        const measuredPrefixWidth = this.namePrefixWidths().get(this.getItemKey(expandedItem)) ?? 0;

        return this.sitemapWidgetHelperService.getNestedContentIndentPx({
            settings: this.siteMapSettings,
            contentIndentPx: this.contentIndentPx(),
            contentLevel: this.contentLevel(),
            expandedItemIndentPx: this.getContentItemIndentPx(expandedItem),
            measuredNamePrefixWidthPx: measuredPrefixWidth,
        });
    }

    protected isContentItemPreviewImageShown(contentItem: ISiteMapContentItem): boolean {
        return this.siteMapHelperService.isContentItemPreviewImageShown(
            this.siteMapSettings.displayThumbnails,
            contentItem,
        );
    }

    protected isMediaBankPreviewIconShown(contentItem: ISiteMapContentItem): boolean {
        return this.siteMapHelperService.isMediaBankPreviewIconShown(
            this.siteMapSettings.displayThumbnails,
            contentItem,
        );
    }

    protected isItemTypeMediaBank(contentItem: ISiteMapContentItem): boolean {
        return this.siteMapHelperService.isObjectTypeMediaBank(contentItem.ObjectType);
    }

    protected isItemTypeDocument(contentItem: ISiteMapContentItem): boolean {
        return this.siteMapHelperService.isObjectTypeDocument(contentItem.ObjectType);
    }

    protected getItemHref(item: ISiteMapContentItem): string {
        switch (item.ObjectType) {
            case ObjectTypes.Document:
                if (this.stateService.params.highlightText) {
                    return this.stateService.href(FiStateNames.Document.View, {
                        Id: item.ObjectId,
                        highlightText: undefined,
                    });
                }
                return this.stateService.href(FiStateNames.Document.View, { Id: item.ObjectId });
            default:
                throw 'FI: Unexpected object type: ' + item.ObjectType;
        }
    }

    protected onFileClick(item: ISiteMapContentItem): void {
        this.mediaBankHelperService.showOrDownloadFile(item.ObjectId);
    }

    protected onExpansionPanelOpened(item: ISiteMapContentItem): void {
        this.setItemExpanded(item, true);
        this.emitTagExpandedChange(item, true);
        setTimeout(() => this.measureNamePrefixWidthsFromDom());
    }

    protected onExpansionPanelClosed(item: ISiteMapContentItem): void {
        this.setItemExpanded(item, false);
        this.emitTagExpandedChange(item, false);
    }

    private isThumbnailLevelWithContentItems(): boolean {
        return this.sitemapWidgetHelperService.hasVisibleContentItems(
            this.parentItem.ChildItems,
            (item: ISiteMapContentItem) => this.isShowChildItem(item) && !this.isMainTagItemHidden(item),
        );
    }

    private emitTagExpandedChange(item: ISiteMapContentItem, expanded: boolean): void {
        if (item.ObjectType !== ObjectTypes.Tag || !item.ChildItems.length) {
            return;
        }

        this.tagExpandedChange.emit({ key: this.getItemKey(item), expanded });
    }

    private setItemExpanded(item: ISiteMapContentItem, expanded: boolean): void {
        const expandedItemKeys = new Set(this.expandedItemKeys());
        const itemKey = this.getItemKey(item);

        if (expanded) {
            expandedItemKeys.add(itemKey);
        } else {
            expandedItemKeys.delete(itemKey);
        }

        this.expandedItemKeys.set(expandedItemKeys);
    }

    private getExpandableChildItemKeys(): string[] {
        return (this.parentItem?.ChildItems ?? [])
            .filter((item: ISiteMapContentItem) => item.ChildItems.length)
            .map((item: ISiteMapContentItem) => this.getItemKey(item));
    }

    private measureNamePrefixWidthsFromDom(): void {
        const widths = new Map<string, number>();

        for (const ref of this.namePrefixSpans) {
            const key = ref.nativeElement.dataset['itemKey'];
            if (key) {
                widths.set(key, ref.nativeElement.offsetWidth);
            }
        }

        const previous = this.namePrefixWidths();
        if (this.sitemapWidgetHelperService.areNumberMapsEqual(previous, widths)) {
            return;
        }

        this.namePrefixWidths.set(widths);
        this.cdr.markForCheck();
    }

    protected readonly FiStateNames = FiStateNames;
    protected readonly fiGetMediaBankFileIcon = fiGetMediaBankFileIcon;
}
